/**
 * API client.
 *
 * Handles token storage, automatic refresh on 401, and turning the backend's
 * error envelope into something a form can render field-by-field.
 */

const API_URL = import.meta.env.VITE_API_URL ?? '';

const ACCESS_TOKEN_KEY = 'unifemga-access-token';
const REFRESH_TOKEN_KEY = 'unifemga-refresh-token';

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly error: string,
    readonly fields: FieldError[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Maps field errors onto React Hook Form's `setError` shape. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.fields.map((f) => [f.field, f.message]));
  }

  /** True when the caller must complete or enrol in two-factor authentication. */
  get requiresTwoFactor(): boolean {
    return this.error === 'TwoFactorRequired' || this.error === 'TwoFactorEnrolmentRequired';
  }
}

const REMEMBER_KEY = 'unifemga-remember';

/**
 * Token storage.
 *
 * Where the tokens live is the whole of "remember me": localStorage survives a
 * browser restart, sessionStorage dies with the tab. Reads check both, because
 * a session started before the preference existed may sit in either.
 */
/** Whether the last sign-in asked to be remembered. Defaults to true. */
function isRemembered(): boolean {
  return localStorage.getItem(REMEMBER_KEY) !== 'false';
}

export const tokens = {
  access: () =>
    sessionStorage.getItem(ACCESS_TOKEN_KEY) ?? localStorage.getItem(ACCESS_TOKEN_KEY),
  refresh: () =>
    sessionStorage.getItem(REFRESH_TOKEN_KEY) ?? localStorage.getItem(REFRESH_TOKEN_KEY),

  remembered: isRemembered,

  set(accessToken: string, refreshToken: string, remember = isRemembered()): void {
    // Clear both first, or switching the preference leaves a stale copy behind
    // in the store that is no longer being written to.
    tokens.clear();
    localStorage.setItem(REMEMBER_KEY, String(remember));

    const store = remember ? localStorage : sessionStorage;
    store.setItem(ACCESS_TOKEN_KEY, accessToken);
    store.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },

  clear(): void {
    for (const store of [localStorage, sessionStorage]) {
      store.removeItem(ACCESS_TOKEN_KEY);
      store.removeItem(REFRESH_TOKEN_KEY);
    }
  },
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skips the refresh-and-retry path. Used by the refresh call itself. */
  skipRefresh?: boolean;
}

/**
 * Shared refresh promise.
 *
 * Without this, a dashboard firing six queries at once would trigger six
 * parallel refreshes — and because refresh tokens rotate, five of them would
 * present an already-rotated token and trip the replay defence, logging the
 * member out. One in-flight refresh, awaited by everyone.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokens.refresh();
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        tokens.clear();
        return false;
      }
      const data = (await response.json()) as { accessToken: string; refreshToken: string };
      tokens.set(data.accessToken, data.refreshToken);
      return true;
    } catch {
      tokens.clear();
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const accessToken = tokens.access();
    return fetch(`${API_URL}${path.startsWith('/api') ? path : `/api/v1${path}`}`, {
      method: options.method ?? 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  };

  let response = await send();

  if (response.status === 401 && !options.skipRefresh && tokens.refresh()) {
    if (await refreshTokens()) {
      response = await send();
    }
  }

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  // A successful response that is not JSON means the request never reached the
  // API. The usual cause is a static host answering an unmatched path with
  // index.html: the SPA catch-all rewrite returns 200, so nothing here would
  // otherwise object, and safeParse hands back the raw HTML as a string. A
  // caller expecting an array then calls .map on it and the page goes blank
  // with no clue as to why. Fail loudly and name the likely cause instead.
  if (response.ok && text && !(response.headers.get('content-type') ?? '').includes('json')) {
    throw new ApiError(
      'The API returned something that is not JSON. This usually means requests are ' +
        'reaching the web host rather than the API — check VITE_API_URL.',
      response.status,
      'NonJsonResponse',
    );
  }

  if (!response.ok) {
    const body = (payload ?? {}) as {
      message?: string;
      error?: string;
      fields?: FieldError[];
      requestId?: string;
    };
    throw new ApiError(
      body.message ?? 'Something went wrong.',
      response.status,
      body.error ?? 'Error',
      body.fields ?? [],
      body.requestId,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
